import styled, { keyframes } from 'styled-components';

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

export const AssessmentWrapper = styled.div`
  width: 100%;
  min-height: calc(100vh - 64px);
  background: ${({ theme }) => theme.landing.background};
  transition: background 0.3s ease;
`;

export const AssessmentContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  margin-top: 64px;
  padding: ${({ theme }) => theme.landingSpacing.xl};

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.lg}) {
    padding: ${({ theme }) => theme.landingSpacing.lg};
  }
  @media (max-width: ${({ theme }) => theme.landingBreakpoints.md}) {
    padding: ${({ theme }) => theme.landingSpacing.md};
  }
  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    padding: ${({ theme }) => theme.landingSpacing.md};
  }
`;

/* ── Header ─────────────────────────────────────────────────────── */

export const HeaderSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.landingSpacing.xl};
  gap: ${({ theme }) => theme.landingSpacing.md};
  flex-wrap: wrap;

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.md}) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const SectionTitle = styled.h1`
  font-weight: 500;
  font-size: 1.75rem;
  line-height: 1.2;
  margin: 0;
  color: ${({ theme }) => theme.landing.text};
  letter-spacing: -0.025em;

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    font-size: 1.5rem;
  }
`;

export const SectionDescription = styled.p`
  font-weight: 400;
  font-size: 1rem;
  line-height: 1.5;
  color: ${({ theme }) => theme.landing.textSecondary};
  margin: 4px 0 0 0;

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    font-size: 0.95rem;
  }
`;

export const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.landingSpacing.sm};

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    width: 100%;
    flex-direction: column;
  }
`;

export const DashboardButton = styled.button`
  background: transparent;
  color: ${({ theme }) => theme.landing.text};
  border: 1px solid ${({ theme }) => theme.landing.cardBorder};
  border-radius: ${({ theme }) => theme.landingBorderRadius.md};
  padding: ${({ theme }) => theme.landingSpacing.sm} ${({ theme }) => theme.landingSpacing.md};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.landing.iconBg};
    border-color: ${({ theme }) => theme.landing.text};
  }
  &:active {
    transform: scale(0.98);
  }
  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    width: 100%;
  }
`;

/* ── Cover / score card ─────────────────────────────────────────── */

export const CoverPanel = styled.div`
  background: ${({ theme }) => theme.landing.cardBg};
  border: 1px solid ${({ theme }) => theme.landing.cardBorder};
  border-radius: ${({ theme }) => theme.landingBorderRadius.lg};
  padding: ${({ theme }) => theme.landingSpacing.lg};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.landingSpacing.lg};
  margin-bottom: ${({ theme }) => theme.landingSpacing.lg};
  flex-wrap: wrap;

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    padding: ${({ theme }) => theme.landingSpacing.md};
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const CoverMetaGrid = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.landingSpacing.xl};
  flex-wrap: wrap;
`;

export const CoverMetaItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const CoverMetaLabel = styled.span`
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.landing.textSecondary};
`;

export const CoverMetaValue = styled.span`
  font-size: 0.9rem;
  font-weight: 600;
  color: ${({ theme }) => theme.landing.text};
`;

export const ScoreRing = styled.div`
  width: 110px;
  height: 110px;
  min-width: 110px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  position: relative;
  background: conic-gradient(
    ${({ $score }) => ($score >= 80 ? '#639922' : $score >= 50 ? '#EF9F27' : '#E24B4A')} 0%
      ${({ $score }) => $score}% ${({ theme }) => theme.landing.cardBorder}
      ${({ $score }) => $score}% 100%
  );

  &::before {
    content: '';
    position: absolute;
    inset: 9px;
    border-radius: 50%;
    background: ${({ theme }) => theme.landing.cardBg};
  }
`;

export const ScoreNum = styled.span`
  position: relative;
  font-size: 1.5rem;
  font-weight: 800;
  color: ${({ theme }) => theme.landing.text};
`;

export const ScoreLabel = styled.span`
  position: relative;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.landing.textSecondary};
`;

/* ── Sections ───────────────────────────────────────────────────── */

export const Section = styled.div`
  background: ${({ theme }) => theme.landing.cardBg};
  border: 1px solid ${({ theme }) => theme.landing.cardBorder};
  border-radius: ${({ theme }) => theme.landingBorderRadius.lg};
  padding: ${({ theme }) => theme.landingSpacing.lg};
  margin-bottom: ${({ theme }) => theme.landingSpacing.lg};

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    padding: ${({ theme }) => theme.landingSpacing.md};
  }
`;

export const SectionLabel = styled.h2`
  font-weight: 600;
  font-size: 1.125rem;
  line-height: 1.4;
  color: ${({ theme }) => theme.landing.text};
  margin: 0 0 ${({ theme }) => theme.landingSpacing.md} 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

export const CountBadge = styled.span`
  font-size: 0.75rem;
  font-weight: 700;
  background: ${({ theme }) => theme.landing.iconBg};
  border: 1px solid ${({ theme }) => theme.landing.cardBorder};
  padding: 4px ${({ theme }) => theme.landingSpacing.sm};
  border-radius: 99px;
  color: ${({ theme }) => theme.landing.textSecondary};
`;

/* ── Raw PO data (assessmentData) ──────────────────────────────── */

export const PoList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.landingSpacing.md};
`;

export const PoItem = styled.div`
  border: 1px solid ${({ theme }) => theme.landing.cardBorder};
  border-radius: ${({ theme }) => theme.landingBorderRadius.md};
  padding: ${({ theme }) => theme.landingSpacing.md};
  background: ${({ theme }) => theme.landing.background};
`;

export const PoCode = styled.span`
  display: inline-block;
  font-size: 0.75rem;
  font-weight: 700;
  color: ${({ theme }) => theme.landing.buttonBg};
  letter-spacing: 0.03em;
  text-transform: uppercase;
  margin-bottom: 6px;
`;

export const PoDescription = styled.p`
  font-size: 0.875rem;
  line-height: 1.55;
  color: ${({ theme }) => theme.landing.text};
  margin: 0 0 ${({ theme }) => theme.landingSpacing.sm} 0;
`;

export const PoFieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 4px;
  padding-top: ${({ theme }) => theme.landingSpacing.sm};
  border-top: 1px solid ${({ theme }) => theme.landing.cardBorder};
`;

export const PoFieldLabel = styled.span`
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.landing.textSecondary};
`;

export const PoFieldValue = styled.p`
  font-size: 0.825rem;
  line-height: 1.55;
  color: ${({ theme }) => theme.landing.textSecondary};
  margin: 2px 0 0 0;
`;

/* ── Findings (precinctAssessmentResult / devAssessmentResult) ──── */

export const FindingList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.landingSpacing.sm};
`;

export const FindingItem = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.landingSpacing.md};
  padding: ${({ theme }) => theme.landingSpacing.md};
  border-radius: ${({ theme }) => theme.landingBorderRadius.md};
  border: 1px solid ${({ theme }) => theme.landing.cardBorder};
  background: ${({ theme }) => theme.landing.background};
`;

export const FindingIcon = styled.div`
  width: 28px;
  height: 28px;
  min-width: 28px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 15px;
    height: 15px;
  }

  ${({ $status }) => {
    if ($status === 'COMPLIANT') return `background: rgba(99,153,34,0.12); color: #639922;`;
    if ($status === 'INSUFFICIENT_EVIDENCE')
      return `background: rgba(239,159,39,0.12); color: #854F0B;`;
    return `background: rgba(226,75,74,0.12); color: #A32D2D;`;
  }}
`;

export const FindingBody = styled.div`
  flex: 1;
  min-width: 0;
`;

export const FindingHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
  flex-wrap: wrap;
`;

export const FindingCode = styled.span`
  font-size: 0.8rem;
  font-weight: 700;
  color: ${({ theme }) => theme.landing.text};
`;

export const StatusBadge = styled.span`
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  padding: 2px 8px;
  border-radius: 99px;

  ${({ $status }) => {
    if ($status === 'COMPLIANT') return `background: rgba(99,153,34,0.12); color: #639922;`;
    if ($status === 'INSUFFICIENT_EVIDENCE')
      return `background: rgba(239,159,39,0.12); color: #854F0B;`;
    return `background: rgba(226,75,74,0.12); color: #A32D2D;`;
  }}
`;

export const FindingDescription = styled.p`
  font-size: 0.8rem;
  line-height: 1.5;
  color: ${({ theme }) => theme.landing.textSecondary};
  margin: 0 0 4px 0;
`;

export const FindingReason = styled.p`
  font-size: 0.8rem;
  line-height: 1.55;
  color: ${({ theme }) => theme.landing.text};
  margin: 0;
`;

/* ── States ─────────────────────────────────────────────────────── */

export const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 3rem;
  font-size: 14px;
  color: ${({ theme }) => theme.landing.textSecondary};

  .spin {
    animation: ${spin} 1s linear infinite;
  }
`;

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 3rem 2rem;
  text-align: center;
  color: ${({ theme }) => theme.landing.textSecondary};
  font-size: 14px;
`;

export const StatusMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  padding: 8px 12px;
  border-radius: ${({ theme }) => theme.landingBorderRadius.md};
  background: ${({ $type, theme }) =>
    $type === 'error' ? 'var(--color-background-danger)' : theme.landing.iconBg};
  color: ${({ $type, theme }) =>
    $type === 'error' ? 'var(--color-text-danger)' : theme.landing.textSecondary};
  border: 0.5px solid
    ${({ $type, theme }) =>
      $type === 'error' ? 'var(--color-border-danger)' : theme.landing.cardBorder};
`;

export const CheckAgainButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 18px;
  font-size: 13px;
  font-weight: 500;
  background: transparent;
  color: ${({ theme }) => theme.landing.text};
  border: 1px solid ${({ theme }) => theme.landing.buttonBg};
  border-radius: ${({ theme }) => theme.landingBorderRadius.md};
  cursor: pointer;
  transition: background 0.15s;
  margin-top: 4px;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.landing.iconBg};
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .spin {
    animation: ${spin} 1s linear infinite;
  }
`;

export const GlobalError = styled.div`
  background: var(--color-background-danger);
  border: 0.5px solid var(--color-border-danger);
  border-radius: var(--border-radius-md);
  padding: 10px 14px;
  font-size: 13px;
  color: var(--color-text-danger);
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: ${({ theme }) => theme.landingSpacing.lg};
`;
